// tolerance for floating point comparison after converting the label percent to a fraction
const RATE_MATCH_TOLERANCE = 0.0001

const getRateFromLabel = (taxRateLabel: string): number | undefined => {
  // extracts the numeric percent from labels like "5,5 %", "5.5%" or "TVA 5,5 %"
  const rateMatch = taxRateLabel.replace(',', '.').match(/(\d+(?:\.\d+)?)/)

  return rateMatch ? parseFloat(rateMatch[1]) / 100 : undefined
}

export interface MatchableTaxRate {
  id: string
  predefinedTag?: string | null
  rate: number
}

interface GetMatchedVatRateIdOptions {
  taxRateId?: string
  taxRateLabel?: string
  vatRates: MatchableTaxRate[]
}

// the review entry API can return a taxRateId which is a predefined tag (e.g. 's_fr_55'),
// while the purchase tax rates from /v2/taxRates have proper entity UUIDs; we first try to
// match by predefinedTag to get the correct UUID, then fall back to label-based rate matching
export const getMatchedVatRateId = (options: GetMatchedVatRateIdOptions): string => {
  const { taxRateId, taxRateLabel, vatRates } = options

  const matchedByPredefinedTag = taxRateId ? vatRates.find((vatRate) => vatRate.predefinedTag === taxRateId) : undefined

  if (matchedByPredefinedTag) {
    return matchedByPredefinedTag.id
  }

  const isKnownVatRateId = !!taxRateId && vatRates.some((vatRate) => vatRate.id === taxRateId)

  if (isKnownVatRateId) {
    return taxRateId
  }

  if (!taxRateLabel) {
    return ''
  }

  const labelRate = getRateFromLabel(taxRateLabel)

  if (labelRate === undefined) {
    return ''
  }

  const matchedVatRate = vatRates.find((vatRate) => Math.abs(vatRate.rate - labelRate) <= RATE_MATCH_TOLERANCE)

  return matchedVatRate?.id ?? ''
}

export const getMatchedTaxRate = (options: GetMatchedVatRateIdOptions): number | undefined => {
  const matchedId = getMatchedVatRateId(options)

  if (matchedId) {
    const matchedRate = options.vatRates.find((vatRate) => vatRate.id === matchedId)?.rate

    if (matchedRate !== undefined) {
      return matchedRate
    }
  }

  if (options.taxRateLabel) {
    return getRateFromLabel(options.taxRateLabel)
  }

  return undefined
}
